import {
  CreatedIssuesDto,
  GetIssueResponseDtoStatusEnum,
} from '@issuemanager/api-client';
import {
  DEFAULT_FILTER_MODEL_STATE,
  useDataGridContext,
  useIssueStatuses,
} from '@issuemanager/apps/client/components';

import { useUserContext } from '@issuemanager/apps/client/contexts';
import { CreatedIssuesTypeEnum } from '@issuemanager/constants';
import {
  CreatedIssuesEnum,
  QuickFilterAllEnum,
} from '@issuemanager/test/constants';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QuickFilterContainer } from './quick-filter-container';
import { QuickFilterItem } from './quick-filter-item';

export function CreatedIssues({
  createdIssues,
  activeFilter,
  setActiveFilter,
}: {
  createdIssues: CreatedIssuesDto | undefined;
  activeFilter: QuickFilterAllEnum | null;
  setActiveFilter: (value: QuickFilterAllEnum) => void;
}): React.JSX.Element {
  const { t } = useTranslation(['issue']);
  const user = useUserContext();
  const context = useDataGridContext();
  const issueStatuses = useIssueStatuses();

  const statusOpen = useMemo(
    () =>
      issueStatuses.find((s) => s.value === GetIssueResponseDtoStatusEnum.Open),
    [issueStatuses],
  );
  const statusInProgress = useMemo(
    () =>
      issueStatuses.find(
        (s) => s.value === GetIssueResponseDtoStatusEnum.InProgress,
      ),
    [issueStatuses],
  );

  const clearFilters = (): void => {
    context.setFilterModel(DEFAULT_FILTER_MODEL_STATE);
  };

  const handleFilterSelection = (type: string): void => {
    clearFilters();
    switch (type) {
      case CreatedIssuesTypeEnum.ALL:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          creator: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            creator: [user.data],
          }),
          'quick-filter',
        );
        break;
      case CreatedIssuesTypeEnum.OPEN:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          creator: true,
          status: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            creator: [user.data],
            status: [statusOpen],
          }),
          'quick-filter',
        );
        break;
      case CreatedIssuesTypeEnum.IN_PROGRESS:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          creator: true,
          status: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            creator: [user.data],
            status: [statusInProgress],
          }),
          'quick-filter',
        );
        break;
    }
  };

  return (
    <QuickFilterContainer label={t('quickFilters.createdIssues.title')}>
      <QuickFilterItem
        label={t('quickFilters.createdIssues.all')}
        number={createdIssues?.all}
        onClick={() => {
          handleFilterSelection(CreatedIssuesTypeEnum.ALL);
          setActiveFilter(CreatedIssuesEnum.ALL);
        }}
        isActive={activeFilter === CreatedIssuesEnum.ALL}
      />
      <QuickFilterItem
        label={t('quickFilters.createdIssues.open')}
        number={createdIssues?.open}
        onClick={() => {
          handleFilterSelection(CreatedIssuesTypeEnum.OPEN);
          setActiveFilter(CreatedIssuesEnum.OPEN);
        }}
        isActive={activeFilter === CreatedIssuesEnum.OPEN}
      />
      <QuickFilterItem
        label={t('quickFilters.createdIssues.inProgress')}
        number={createdIssues?.inProgress}
        onClick={() => {
          handleFilterSelection(CreatedIssuesTypeEnum.IN_PROGRESS);
          setActiveFilter(CreatedIssuesEnum.IN_PROGRESS);
        }}
        isActive={activeFilter === CreatedIssuesEnum.IN_PROGRESS}
      />
    </QuickFilterContainer>
  );
}
